import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { type FC, useEffect, useMemo, useState } from "react";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { InspectionPhoto } from "../../api/dto/InspectionPhoto";
import { AddInspectionPhotosModal } from "../../modals/AddInspectionPhotosModal";
import { Card } from "../Card";
import { Button } from "../form/Button";
import { Loading } from "../Loading";
import { useModal } from "../layout/ModalProvider";
import { useRefreshEffect } from "../RefreshController";
import { InspectionPhotoLayoutIndicator } from "./InspectionPhotoLayoutIndicator";
import { InspectionPhotoOverviewCard } from "./InspectionPhotoOverviewCard";
import type { EnrichedInspection } from "./types";

export const InspectionPhotosOverview: FC<{
  inspection: EnrichedInspection;
  isCustomer: boolean;
}> = (props) => {
  const { listPhotos, updatePhotoOrder } = useApiCall();
  const photosResource = useFetchedResource((inspectionId: string) => listPhotos(inspectionId));
  const [photos, setPhotos] = useState<InspectionPhoto[]>([]);
  useRefreshEffect(() => {
    photosResource.reload(props.inspection.id);
  });
  useEffect(() => {
    if (photosResource.resource !== null) {
      setPhotos(photosResource.resource?.sort((a, b) => a.order - b.order));
    }
  }, [photosResource.resource]);

  const hasLocalOrderChanges = useMemo(() => {
    const remoteItems = [...(photosResource.resource ?? [])];
    const remoteOrder = remoteItems.sort((a, b) => a.order - b.order).map((p) => p.id);
    const localItems = [...photos];
    const localOrder = localItems.map((p) => p.id);

    return JSON.stringify(remoteOrder) !== JSON.stringify(localOrder);
  }, [photosResource.resource, photos]);

  const updateOrder = async () => {
    await updatePhotoOrder(
      props.inspection.id,
      photos.map((p) => p.id),
    );
    await photosResource.reload(props.inspection.id);
  };

  const addInspectionPhotosModal = useModal({
    title: "Fotos toevoegen",
    body: (
      <AddInspectionPhotosModal
        inspection={props.inspection}
        onFinished={() => photosResource?.reload(props.inspection.id, true)}
      />
    ),
  });

  return (
    <Card title={"Foto's"}>
      <Loading loading={photosResource.loading}>
        {hasLocalOrderChanges && (
          <div
            className={"mb-3 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 rounded"}
          >
            <p className={"mb-2"}>Je hebt onopgeslagen wijzigingen in de volgorde van de foto's.</p>
            <Button
              type={"primary"}
              size={"sm"}
              text={"Wijzigingen opslaan"}
              onClick={updateOrder}
            />
          </div>
        )}
        <div className={"flex items-stretch"}>
          <div className={"flex-1 "}>
            <div className={"flex flex-wrap"}>
              {photos.map((photo, i) => {
                const movePrev =
                  i === 0
                    ? undefined
                    : () => {
                        setPhotos((old) =>
                          old.map((p, j) => {
                            return j === i - 1 ? photo : j === i ? old[i - 1] : p;
                          }),
                        );
                      };
                const moveNext =
                  i === photos.length - 1
                    ? undefined
                    : () => {
                        setPhotos((old) =>
                          old.map((p, j) => (j === i + 1 ? photo : j === i ? old[i + 1] : p)),
                        );
                      };
                const moveToFront =
                  i === 0
                    ? undefined
                    : () => {
                        setPhotos((old) => {
                          const newOrder = old.filter((_, j) => j !== i);
                          newOrder.unshift(photo);
                          return newOrder;
                        });
                      };
                const moveToBack =
                  i === photos.length - 1
                    ? undefined
                    : () => {
                        setPhotos((old) => {
                          const newOrder = old.filter((_, j) => j !== i);
                          newOrder.push(photo);
                          return newOrder;
                        });
                      };
                const onUpdate = (
                  data: Pick<InspectionPhoto, "rotation" | "description" | "questionId">,
                ) => {
                  setPhotos((old) =>
                    old.map((p) => {
                      if (photo.id === p.id) {
                        return {
                          ...p,
                          ...data,
                        };
                      } else {
                        return p;
                      }
                    }),
                  );
                };
                const onDelete = () => {
                  setPhotos((old) => old.filter((v) => v.id !== photo.id));
                  photosResource.reload(props.inspection.id, true);
                };

                return (
                  <InspectionPhotoOverviewCard
                    key={i}
                    photo={photo}
                    questions={props.inspection.questionSet.questions}
                    prev={movePrev}
                    next={moveNext}
                    toFront={moveToFront}
                    toBack={moveToBack}
                    update={onUpdate}
                    delete={onDelete}
                    reload={async () => {
                      await photosResource.reload(photo.inspectionId);
                    }}
                  />
                );
              })}
            </div>
            {hasLocalOrderChanges && (
              <div
                className={
                  "mt-3 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 rounded"
                }
              >
                <p className={"mb-2"}>
                  Je hebt onopgeslagen wijzigingen in de volgorde van de foto's.
                </p>
                <Button
                  type={"primary"}
                  size={"sm"}
                  text={"Wijzigingen opslaan"}
                  onClick={updateOrder}
                />
              </div>
            )}
          </div>
          <div className={"relative"}>
            <div className={"sticky top-4 border border-slate-200 p-2 rounded-lg"}>
              <div className={"text-sm font-medium"}>Voorbeeldweergave</div>
              <InspectionPhotoLayoutIndicator photos={photos} />
            </div>
          </div>
        </div>
      </Loading>
      <Button
        type={"primary"}
        icon={faUpload}
        text={"Foto(s) uploaden"}
        size={"md"}
        onClick={() => addInspectionPhotosModal.open()}
      />
    </Card>
  );
};
